import { useEffect, useMemo, useState } from 'react';
import { fetchCustomerContactsRequest, fetchLeadContactsRequest } from '@/services/api/contact';
import { processAPIResponseNow } from '@/utils/request';
import { ContactModel } from '@/typings/models/Contact';
import { getContactName } from '@/utils/contacts';

type ContactChoices = {
  /** `null` while loading. */
  contacts: ContactModel[] | null;
  /** The customer's primary contact, or the project's. */
  defaultId?: string;
};

/**
 * The contacts to pick from for a customer or a project (a project's are its
 * customer's, its own primary first). Picking is optional, so a failed load
 * just leaves nothing to pick.
 */
export const useContactChoices = ({ customerId, leadId }: { customerId?: string; leadId?: string }): ContactChoices => {
  const [contacts, setContacts] = useState<ContactModel[] | null>(null);

  useEffect(() => {
    setContacts(null);
    if (!customerId && !leadId) return undefined;
    let active = true;
    const request = leadId
      ? fetchLeadContactsRequest({ params: { leadId } })
      : fetchCustomerContactsRequest({ params: { customerId: customerId as string } });
    request.then((response) =>
      processAPIResponseNow(
        response,
        {
          onSuccess: (list: ContactModel[]) => active && setContacts(Array.isArray(list) ? list : []),
          onError: () => active && setContacts([]),
        },
        'data',
      ),
    );
    return () => {
      active = false;
    };
  }, [customerId, leadId]);

  const defaultId = useMemo(
    () => (leadId ? contacts?.[0] : contacts?.find((contact) => contact.is_primary) || contacts?.[0])?.id,
    [contacts, leadId],
  );

  return { contacts, defaultId };
};

/** Select options: the name, with the role at the customer when there is one. */
export const contactChoiceOptions = (contacts: ContactModel[]) =>
  contacts.map((contact) => ({
    value: contact.id,
    label: contact.role ? `${getContactName(contact)} · ${contact.role}` : getContactName(contact),
  }));
